import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { Button } from '@totango-app-team/ui-shared-components';
import get from 'lodash/get';

import Input from 'Components/input/input';
import RecaptchaWidget from 'Components/recaptcha-widget/recaptcha-widget';
import useFetchCaptchaToken from 'Hooks/useFetchCaptchaToken';
import { CAPTCHA_FF } from 'Utils/constants';
import { domainCaptchaBlacklist, getEmailDomain, isLocal } from 'Utils/utils';

import OauthSignupButton from '../oauth/oauth-signup-button';
import { gaPushEvent, SIGNUP_METHOD_TYPES } from '../tracking/google-analytics';
import { trackHubspotSignupDetailes } from '../tracking/hubspot';
import {
	checkSignup880FeatureEnabled,
	validateHumanUserAvailability,
	validateUserAvailability,
} from '../utils/api';
import EmailValidationError from '../utils/email-validation-error';
import useDecryptEmail from '../utils/useDecryptEmail';
import { isCommunity } from '../utils/utils';

const atLeastEightChars = password => password.length > 7;
const atLeastOneLower = password => /(.*[a-z].*)/.test(password);
const atLeastOneUpper = password => /(.*[A-Z].*)/.test(password);
const atLeastOneNumberOrSymbol = password =>
	/(.*[0-9\!\@\#\$\%\^\&\*\(\)\±\~\?\{\}\|\”\<\>].*)/.test(password);

const getEmailSubdomain = email =>
	email.indexOf('@') !== -1 ? email.split('@')[1].split('.')[0] : '';

export default function CreateAccount(props) {
	const {
		handleNext,
		title,
		email,
		setEmail,
		password,
		setPassword,
		accountName,
		setAccountName,
		setIsLoading,
		isActiveStep,
		setFirstName,
		setLastName,
		setProfileImageUrl,
		setEmailHash,
		setForceUpdateHeight,
		oauthIssuer,
		setOauthIssuer,
	} = props;
	const [validationErrorReason, setValidationErrorReason] = useState('');
	const [isValidPassword, setIsValidPassword] = useState(false);
	const [showPasswordValidation, setShowPasswordValidation] = useState(false);
	const { fetchToken, captchaRef } = useFetchCaptchaToken();
	const [captchaEnabled, setCaptchaEnabled] = useState(null);
	const inputRef = useRef();
	const passwordRef = useRef();

	const passwordFocus = useCallback(() => {
		setShowPasswordValidation(true);
	}, []);

	const { isDecryptingEmail, decryptedEmail, decryptionError } = useDecryptEmail();
	useEffect(() => {
		if (decryptedEmail) {
			setEmail(decryptedEmail);
		}
	}, [decryptedEmail, setEmail]);
	useEffect(() => {
		if (decryptionError) {
			setValidationErrorReason(decryptionError);
		}
	}, [decryptionError]);

	const onInputChange = useCallback(
		e => {
			const emailAddress = e.target.value.toLowerCase();
			const emailSubdomain = getEmailSubdomain(emailAddress);
			setEmail(emailAddress);
			setAccountName(emailSubdomain);
			setValidationErrorReason('');
		},
		[setAccountName, setEmail],
	);

	useEffect(() => {
		const fetchFeatureEnabled = async () => {
			try {
				const featureEnabled = await checkSignup880FeatureEnabled(CAPTCHA_FF);
				setCaptchaEnabled(featureEnabled);
			} catch (err) {
				console.error('Failed to fetch feature enabled', err);
			}
		};

		fetchFeatureEnabled();
	}, []);

	const isRecaptchaFeatureEnabled = captchaEnabled === true || captchaEnabled?.data === true;

	const onPasswordChange = useCallback(
		e => {
			e.persist();
			setPassword(e.target.value);
		},
		[setPassword],
	);

	const onButtonClick = useCallback(
		e => {
			const validateUsername = async username => {
				if (
					!/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
						username,
					)
				) {
					setIsLoading(false);
					setValidationErrorReason('invalid');
					return;
				}
				if (!oauthIssuer && !isValidPassword) {
					setIsLoading(false);
					setValidationErrorReason('invalid');
					setIsValidPassword(false);
					return;
				}
				try {
					let validation = null;
					const domain = getEmailDomain(username);
					if (isRecaptchaFeatureEnabled && !domainCaptchaBlacklist.includes(domain)) {
						const token = await fetchToken();
						validation =
							typeof token === 'string'
								? await validateHumanUserAvailability(username, token)
								: await validateUserAvailability(username);
					} else {
						validation = await validateUserAvailability(username);
					}
					if (validation.is_available) {
						setIsLoading(false);
						const signUpMethod = oauthIssuer
							? SIGNUP_METHOD_TYPES.SSO
							: SIGNUP_METHOD_TYPES.STANDARD;
						gaPushEvent({
							stepIndex: 1,
							isSuccess: false,
							isFreeTrial: !isCommunity,
							signUpMethod,
						});
						handleNext();
					} else {
						setEmail('');
						setOauthIssuer(null);
						setIsLoading(false);
						setValidationErrorReason(validation.reason);
					}
				} catch (err) {
					setIsLoading(false);
					setValidationErrorReason('general');
				}
			};
			setValidationErrorReason('');
			setIsLoading(true);
			validateUsername(email);
			trackHubspotSignupDetailes(email);
		},
		[
			setIsLoading,
			email,
			oauthIssuer,
			isValidPassword,
			isRecaptchaFeatureEnabled,
			fetchToken,
			handleNext,
			setEmail,
			setOauthIssuer,
		],
	);

	const onKeyUpHandler = useCallback(
		e => {
			if (e && e.keyCode === 13 && isActiveStep && isValidPassword) {
				onButtonClick();
			}
		},
		[isActiveStep, onButtonClick, isValidPassword],
	);

	const tabIndex = useMemo(() => (!isActiveStep ? { tabIndex: -1 } : {}), [isActiveStep]);

	useEffect(() => {
		if (isActiveStep && !oauthIssuer) {
			inputRef.current.focus();
		}
	}, [isActiveStep, oauthIssuer]);

	useEffect(() => {
		if (validationErrorReason) {
			setForceUpdateHeight(true);
		}
	}, [setForceUpdateHeight, validationErrorReason]);

	const onOauthSignInSuccess = useCallback(
		(issuer, user = {}) => {
			setEmail(user.email.toLowerCase());
			setAccountName(getEmailSubdomain(user.email));
			setFirstName(get(user, 'firstName'));
			setLastName(get(user, 'lastName'));
			setProfileImageUrl(get(user, 'profileImageUrl'));
			setEmailHash(get(user, 'emailHash'));
			setOauthIssuer(issuer);
		},
		[
			setAccountName,
			setEmail,
			setFirstName,
			setLastName,
			setOauthIssuer,
			setProfileImageUrl,
			setEmailHash,
		],
	);

	useEffect(() => {
		if (oauthIssuer && accountName && email && isActiveStep) {
			onButtonClick();
		}
	}, [oauthIssuer, accountName, email, onButtonClick, isActiveStep]);

	const onOauthError = useCallback(error => {
		console.error(error);
		setValidationErrorReason('general');
	}, []);

	function passwordRule(label, isChecked) {
		return (
			<div className="password-rule">
				{isChecked ? (
					<i className="rule-icon fa fa-check-circle" />
				) : (
					<i className="rule-icon fa fa-circle-thin" />
				)}
				<span className="rule-label">{label}</span>
			</div>
		);
	}

	useEffect(() => {
		// password validation
		const validation =
			atLeastEightChars(password) &&
			atLeastOneLower(password) &&
			atLeastOneUpper(password) &&
			atLeastOneNumberOrSymbol(password);
		setIsValidPassword(validation);
	}, [password]);

	return (
		<Grid container spacing={4} className="step-container create-account">
			<Grid item xs={12}>
				<h5 className="step-title" align="center">
					{title}
				</h5>
				<div className="step-description create-account-description">
					{isCommunity ? "It's Free!" : "It's free forever. No credit card needed."}
				</div>
			</Grid>
			<Grid item xs={12}>
				{!!validationErrorReason && <EmailValidationError reason={validationErrorReason} />}
				<Input
					id="totango-signup-email"
					variant="outlined"
					value={email}
					label="Work Email"
					fullWidth
					autoComplete="no"
					onChange={onInputChange}
					disabled={isDecryptingEmail}
					error={!!validationErrorReason && !oauthIssuer}
					inputProps={tabIndex}
					inputRef={inputRef}
					onKeyUp={onKeyUpHandler}
					maxLength={100}
				/>
			</Grid>
			<Grid item xs={12}>
				<Input
					id="signup-password"
					name="password"
					variant="outlined"
					type="password"
					value={password}
					label="Create a Password"
					fullWidth
					autoComplete="no"
					onChange={onPasswordChange}
					onKeyUp={onKeyUpHandler}
					inputProps={tabIndex}
					inputRef={passwordRef}
					error={!isValidPassword && password !== ''}
					onClick={passwordFocus}
					onFocus={passwordFocus}
					maxLength={255}
				/>
			</Grid>
			{showPasswordValidation && (
				<Grid item xs={12}>
					<div className="password-validation">
						{passwordRule('At least 8 characters', atLeastEightChars(password))}
						{passwordRule('One lowercase letter', atLeastOneLower(password))}
						{passwordRule('One uppercase letter', atLeastOneUpper(password))}
						{passwordRule('One number or symbol', atLeastOneNumberOrSymbol(password))}
					</div>
				</Grid>
			)}
			<Grid item xs={12}>
				<Button
					color="primary"
					variant="contained"
					label="Continue"
					onClick={onButtonClick}
					onKeyUp={onKeyUpHandler}
					disabled={isDecryptingEmail || (isValidPassword && email.length <= 0)}
					fullWidth
					{...(!isActiveStep ? { tabIndex: -1 } : {})}
				>
					{'Continue'}
				</Button>
			</Grid>
			<Grid item xs={12} align="center" className="separator">
				OR
			</Grid>
			<Grid container justifyContent="space-evenly">
				{['google', 'slack', 'microsoft'].map(type => (
					<OauthSignupButton
						key={type}
						type={type}
						onSuccess={onOauthSignInSuccess}
						onFailure={onOauthError}
					/>
				))}
			</Grid>
			<Grid item xs={12}>
				<div className="signin-btn-tooltip">
					Already have an account?
					<a
						className="login-link"
						href={
							isLocal()
								? 'https://localhost:8443/debug.html'
								: `https://${window.location.hostname}`
						}
					>
						<u> Login </u>
					</a>
				</div>
			</Grid>
			{isRecaptchaFeatureEnabled && <RecaptchaWidget ref={captchaRef} />}
		</Grid>
	);
}
